<template lang="pug">
.notification-container(:style="containerStyle")
  transition-group(name="notification" tag="div")
    .notification(
      v-for="notification in notifications"
      :key="notification.id"
      :class="[`notification--${notification.type}`]"
      @click="removeNotification(notification.id)"
    )
      .notification__icon
        LucideIcon(
          v-if="notification.type === 'success'"
          name="CheckCircle"
          :size="20"
        )
        LucideIcon(
          v-else-if="notification.type === 'error'"
          name="XCircle"
          :size="20"
        )
        LucideIcon(
          v-else-if="notification.type === 'warning'"
          name="AlertCircle"
          :size="20"
        )
        LucideIcon(
          v-else-if="notification.type === 'info'"
          name="Info"
          :size="20"
        )
      
      .notification__content
        .notification__message {{ notification.message }}
      
      .notification__close(@click.stop="removeNotification(notification.id)")
        LucideIcon(name="X" :size="16")
</template>

<script>
import LucideIcon from '@/components/LucideIcon.vue'

export default {
  name: 'CustomNotification',
  components: {
    LucideIcon
  },
  
  data() {
    return {
      notifications: [],
      isMobile: false
    }
  },

  computed: {
    containerStyle() {
      if (this.isMobile) {
        return { left: '20px', right: '20px', width: 'auto', top: '60px' }
      }
      return { right: '20px', top: '80px' }
    }
  },

  mounted() {
    this._checkMobile()
    window.addEventListener('resize', this._checkMobile)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this._checkMobile)
  },

  methods: {
    _checkMobile() {
      this.isMobile = window.innerWidth <= 600
    },

    addNotification(message, type = 'info', duration = 4000) {
      const id = Date.now() + Math.random()
      const notification = {
        id,
        message,
        type,
        duration
      }
      
      this.notifications.push(notification)
      
      // Auto remove after duration
      if (duration > 0) {
        setTimeout(() => {
          this.removeNotification(id)
        }, duration)
      }
      
      return id
    },
    
    removeNotification(id) {
      const index = this.notifications.findIndex(n => n.id === id)
      if (index > -1) {
        this.notifications.splice(index, 1)
      }
    },
    
    // Métodos específicos para cada tipo
    showSuccess(message, duration = 4000) {
      return this.addNotification(message, 'success', duration)
    },
    
    showError(message, duration = 6000) {
      return this.addNotification(message, 'error', duration)
    },
    
    showWarning(message, duration = 5000) {
      return this.addNotification(message, 'warning', duration)
    },
    
    showInfo(message, duration = 4000) {
      return this.addNotification(message, 'info', duration)
    },
    
    clear() {
      this.notifications = []
    }
  }
}
</script>

<style lang="scss" scoped>
.notification-container {
  position: fixed;
  z-index: 9999;
  pointer-events: none;

  @media screen and (max-width: 600px) {
    top: 60px;
    left: 0;
    right: 0;
    width: calc(100% - 40px);
    margin: 0 auto;
  }
  
  .notification {
    min-width: 380px;
    max-width: 500px;
    margin-bottom: 16px;
    padding: 16px 20px;
    border-radius: 12px;
    backdrop-filter: blur(20px);
    background: rgba(30, 30, 30, 0.9);
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
    pointer-events: auto;
    position: relative;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    
    // Efeito de brilho sutil
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent);
      transition: left 0.6s ease;
    }
    
    &:hover {
      transform: translateY(-2px) scale(1.02);
      
      &::before {
        left: 100%;
      }
    }
    
    &__icon {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      filter: drop-shadow(0 0 8px currentColor);
    }
    
    &__content {
      flex: 1;
      min-width: 0;
    }
    
    &__message {
      color: #e5e7eb;
      font-size: 14px;
      font-weight: 500;
      line-height: 1.5;
      word-wrap: break-word;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    }
    
    &__close {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      color: rgba(229, 231, 235, 0.6);
      cursor: pointer;
      border-radius: 4px;
      transition: all 0.2s ease;
      
      &:hover {
        color: rgba(229, 231, 235, 0.9);
        background: rgba(255, 255, 255, 0.1);
        transform: scale(1.1);
      }
    }
    
    // Tipos de notificação
    &--success {
      background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.25) 100%);
      border-left: 4px solid #10b981;
      box-shadow: 0 10px 40px rgba(16, 185, 129, 0.15), 0 0 0 1px rgba(16, 185, 129, 0.2);
      backdrop-filter: blur(20px);
      animation: successPulse 2s ease-in-out;
      
      .notification__icon {
        color: #10b981;
      }
    }
    
    &--error {
      background: linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.25) 100%);
      border-left: 4px solid #ef4444;
      box-shadow: 0 10px 40px rgba(239, 68, 68, 0.15), 0 0 0 1px rgba(239, 68, 68, 0.2);
      backdrop-filter: blur(20px);
      animation: errorShake 0.5s ease-in-out;
      
      .notification__icon {
        color: #ef4444;
      }
    }
    
    &--warning {
      background: linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.25) 100%);
      border-left: 4px solid #f59e0b;
      box-shadow: 0 10px 40px rgba(245, 158, 11, 0.15), 0 0 0 1px rgba(245, 158, 11, 0.2);
      backdrop-filter: blur(20px);
      
      .notification__icon {
        color: #f59e0b;
      }
    }
    
    &--info {
      background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.25) 100%);
      border-left: 4px solid #3b82f6;
      box-shadow: 0 10px 40px rgba(59, 130, 246, 0.15), 0 0 0 1px rgba(59, 130, 246, 0.2);
      backdrop-filter: blur(20px);
      
      .notification__icon {
        color: #3b82f6;
      }
    }

    @media screen and (max-width: 600px) {
      min-width: auto;
      max-width: none;
      width: 100%;
      padding: 14px 16px;
      border-radius: 8px;

      &__message {
        font-size: 13px;
      }
    }
  }
}

// Animações
.notification-enter-active {
  animation: notificationSlideIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.notification-leave-active {
  animation: notificationSlideOut 0.3s ease-in;
}

.notification-move {
  transition: transform 0.3s ease;
}

@keyframes notificationSlideIn {
  0% {
    transform: translateX(100%) scale(0.8) rotateY(-10deg);
    opacity: 0;
  }
  50% {
    transform: translateX(-5px) scale(1.02) rotateY(0deg);
  }
  100% {
    transform: translateX(0) scale(1) rotateY(0deg);
    opacity: 1;
  }
}

@keyframes notificationSlideOut {
  0% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translateX(100%) scale(0.95);
    opacity: 0;
  }
}

@keyframes successPulse {
  0%, 100% {
    box-shadow: 0 10px 40px rgba(16, 185, 129, 0.15), 0 0 0 1px rgba(16, 185, 129, 0.2);
  }
  50% {
    box-shadow: 0 10px 40px rgba(16, 185, 129, 0.25), 0 0 0 1px rgba(16, 185, 129, 0.4), 0 0 20px rgba(16, 185, 129, 0.15);
  }
}

@keyframes errorShake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-3px); }
  75% { transform: translateX(3px); }
}
</style>

<style lang="scss">
@media screen and (max-width: 600px) {
  .notification-container {
    top: 60px !important;
    left: 0 !important;
    right: 0 !important;
    width: calc(100% - 40px) !important;
    margin: 0 auto !important;

    .notification {
      min-width: auto !important;
      max-width: none !important;
      width: 100% !important;
    }
  }
}
</style>